import React from 'react';

import clsx from 'clsx';

import { useLocalizedStringFormatter } from '@coursera/cds-common';
import type { BaseComponentProps } from '@coursera/cds-common';
import { CloseIcon } from '@coursera/cds-icons';

import Button from '@core/Button';
import type { ButtonProps } from '@core/Button';
import i18nMessages from '@core/drawers/TemporaryDrawer/i18n';
import { useTemporaryDrawerContext } from '@core/drawers/TemporaryDrawer/TemporaryDrawerContextProvider';
import Typography from '@core/Typography2';

import getHeaderCss, { classes } from './styles/getHeadingGroupCss';

export type Props = {
  children: React.ReactNode;

  /**
   * The text to display as drawer heading support text
   */
  supportText?: string;

  /**
   * Additional action that will be placed in the heading.
   * Can be used for a Back button.
   */
  backAction?: React.ReactElement<ButtonProps>;
} & BaseComponentProps<'div'>;

const HeadingGroup = React.forwardRef(function HeadingGroup(
  props: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const { children, supportText, backAction, className, id, ...rest } = props;

  const { onClose, headingGroupId } = useTemporaryDrawerContext();

  const stringFormatter = useLocalizedStringFormatter(i18nMessages);

  return (
    <div
      ref={ref}
      className={clsx(className, {
        [classes.hasBackAction]: backAction !== undefined,
      })}
      css={getHeaderCss}
      {...rest}
    >
      <div className={classes.header} id={id ?? headingGroupId}>
        <Typography
          className={classes.heading}
          component="h2"
          variant="titleSmall"
        >
          {children}
        </Typography>

        {supportText && (
          <Typography className={classes.subheading} component="p">
            {supportText}
          </Typography>
        )}
      </div>
      <div className={classes.toolbar}>
        <div className={classes.toolbarStartCol}>{backAction}</div>
        <div className={classes.toolbarEndCol}>
          <Button
            icon={<CloseIcon size="small" />}
            iconPosition="after"
            size="small"
            variant="ghost"
            onClick={onClose}
          >
            {stringFormatter.format('close')}
          </Button>
        </div>
      </div>
    </div>
  );
});

if (__DEV__) {
  HeadingGroup.displayName = 'TemporaryDrawer.HeadingGroup';
}

export default HeadingGroup;
